import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${window.location.origin}/api`;

function UnsubscribePage() {
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [registration, setRegistration] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setStatus("error");
      setMessage("Invalid unsubscribe link. The link may have expired.");
      return;
    }

    // Call unsubscribe API
    axios.get(`${API}/reminders/unsubscribe`, { params: { token } })
      .then(response => {
        setStatus("success");
        setMessage(response.data.message);
        // Extract registration from message if present
        const match = response.data.message?.match(/for ([A-Z0-9]+)/);
        if (match) setRegistration(match[1]);
      })
      .catch(err => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Failed to unsubscribe. The link may be invalid or expired.");
      });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="py-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Processing your request...</p>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Unsubscribed</h1>
              <p className="text-gray-600 mb-6">
                {registration ? (
                  <>You have been unsubscribed from reminders for <strong>{registration}</strong>.</>
                ) : (
                  <>You have been unsubscribed from this reminder.</>
                )}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                You can re-enable reminders anytime from your dashboard.
              </p>
              <a href="/dashboard">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              </a>
            </>
          )}
          
          {status === "error" && (
            <>
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-2">
                <a href="/dashboard">
                  <Button className="bg-blue-600 hover:bg-blue-700 w-full">
                    Go to Dashboard
                  </Button>
                </a>
                <a href="/">
                  <Button variant="outline" className="w-full">
                    Go to Homepage
                  </Button>
                </a>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default UnsubscribePage;
